/**
 * Mercado de ALZ (RMT) — lado COMPRADOR. 2026-09-26.
 *
 * Só ALZ por enquanto. A compra de ITENS (getItems/buyItem/ItemIcon/itemBadges)
 * continua na API e ficou COMENTADA no fim deste arquivo, pra religar depois.
 *
 * Sandbox ligado  -> "Comprar" entrega na hora, de graça (teste).
 * Sandbox desligado -> gera QR Pix EFI (RmtPixDialog); pagou, o ALZ vai pelo correio.
 * Quem vê a tela: Modo Teste ligado = só conta GM (o menu e a API barram o resto).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrendingUp, TrendingDown, Loader2, ShoppingCart, Sparkles, Coins, Users, Activity,
  QrCode, Mail, ShieldCheck, Zap, Clock, ReceiptText, RefreshCw, FlaskConical, Minus, Plus,
} from 'lucide-react';
import {
  getVendors, buyAlz, getMarketStats, getMyOrders,
  type RmtVendor, type RmtMarketStats, type RmtOrder, type RmtPeriod, type RmtBuyResult,
} from '@/shared/services/rmtService';
import { getCharacters, type Character } from '@/shared/services/characterService';
import { useToast } from '@/shared/contexts/ToastContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { PriceChart } from '@/shared/components/rmt/RmtCharts';
import '@/shared/components/rmt/rmt.css';
import RmtPixDialog from './RmtPixDialog';

function trim(x: number): string {
  return Number(x.toFixed(2)).toString();
}
export function fmtAlzShort(n: number): string {
  if (!n) return '0';
  if (n >= 1e9) return trim(n / 1e9) + 'B';
  if (n >= 1e6) return trim(n / 1e6) + 'kk';
  if (n >= 1e3) return trim(n / 1e3) + 'k';
  return n.toLocaleString('pt-BR');
}
const fmtBrl = (n: number) =>
  'R$ ' + (n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTime = (s: string) => new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const errMsg = (e: any, fb: string) =>
  (typeof e?.detail === 'string' ? e.detail : e?.detail?.Message) || e?.message || fb;

// A API (pydantic to_camel) serializa CharacterIdx como 'characteridx'.
function charId(c: Character): number {
  return Number((c as any).characteridx ?? (c as any).characterIdx ?? 0);
}

const ORDER_STATUS: Record<string, { label: string; cls: string }> = {
  completed: { label: 'Entregue', cls: 'bg-emerald-500/15 text-emerald-400' },
  awaiting_payment: { label: 'Aguardando Pix', cls: 'bg-amber-500/15 text-amber-300' },
  expired: { label: 'Vencido', cls: 'bg-white/10 text-white/50' },
  cancelled: { label: 'Cancelado', cls: 'bg-white/10 text-white/50' },
  paid_no_stock: { label: 'Pago · reembolso', cls: 'bg-red-500/15 text-red-300' },
};

function Kpi({ icon, label, value, sub, accent }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string;
}) {
  return (
    <div className="rmt-kpi rounded-xl p-3.5 min-w-0">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/45">
        <span className={accent || 'text-[#8fb9de]'}>{icon}</span>{label}
      </div>
      <div className="text-lg md:text-xl font-black text-white mt-1 truncate tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-white/45 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

export default function MarketplacePage() {
  const { notify } = useToast();
  const { account } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<RmtPeriod>('30d');
  const [stats, setStats] = useState<RmtMarketStats | null>(null);
  const [vendors, setVendors] = useState<RmtVendor[]>([]);
  const [orders, setOrders] = useState<RmtOrder[]>([]);
  const [chars, setChars] = useState<Character[]>([]);
  const [charIdx, setCharIdx] = useState<number | null>(null);
  const [vendorUserNum, setVendorUserNum] = useState<number | null>(null);
  const [units, setUnits] = useState(1);
  const [buying, setBuying] = useState(false);
  const [pix, setPix] = useState<{ orderId: number; initial?: RmtBuyResult | null } | null>(null);

  const reload = useCallback(async (p: RmtPeriod = period) => {
    try {
      const [st, vs, os] = await Promise.all([getMarketStats(p), getVendors(), getMyOrders(20)]);
      setStats(st); setVendors(vs); setOrders(os);
    } catch (e: any) {
      notify('error', errMsg(e, 'Erro carregando o mercado'));
    }
  }, [period, notify]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setChars(await getCharacters()); } catch { /* sem chars */ }
      await reload(period);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (!loading) getMarketStats(period).then(setStats).catch(() => {}); }, [period, loading]);

  useEffect(() => {
    if (charIdx === null && chars.length > 0) setCharIdx(charId(chars[0]));
  }, [chars, charIdx]);

  // livro de ofertas: mais barato primeiro
  const book = useMemo(() => [...vendors].sort((a, b) => a.alzPriceBrl - b.alzPriceBrl || b.availableAlz - a.availableAlz), [vendors]);
  const maxDepth = Math.max(1, ...book.map(v => v.availableAlz));
  useEffect(() => {
    if (book.length === 0) { setVendorUserNum(null); return; }
    if (vendorUserNum === null || !book.some(v => v.userNum === vendorUserNum)) setVendorUserNum(book[0].userNum);
  }, [book, vendorUserNum]);

  const unitAlz = stats?.unitAlz || 100_000_000;
  const sel = book.find(v => v.userNum === vendorUserNum) ?? null;
  const maxUnits = sel ? Math.max(1, Math.min(1000, Math.floor(sel.availableAlz / unitAlz))) : 1;
  useEffect(() => { setUnits(u => Math.min(Math.max(1, u), maxUnits)); }, [maxUnits]);
  const totalAlz = units * unitAlz;
  const totalBrl = sel ? Math.round(units * sel.alzPriceBrl * 100) / 100 : 0;
  const sandbox = stats?.sandbox ?? true;
  const own = sel && account?.userNum === sel.userNum;

  // variacao: 1o x ultimo ponto com preco
  const priced = (stats?.points || []).filter(p => p.price > 0);
  const change = priced.length >= 2 ? ((priced[priced.length - 1].price - priced[0].price) / priced[0].price) * 100 : 0;
  const awaiting = orders.filter(o => o.status === 'awaiting_payment');

  const handleBuy = async () => {
    if (!sel) { notify('error', 'Escolha uma oferta'); return; }
    if (!charIdx) { notify('error', 'Selecione um personagem'); return; }
    setBuying(true);
    try {
      const res = await buyAlz(units, charIdx, sel.userNum, sel.alzPriceBrl);
      if (res.status === 'awaiting_payment') {
        setPix({ orderId: res.orderId, initial: res });
      } else {
        notify('success', res.message);
      }
      await reload();
    } catch (e: any) {
      notify('error', errMsg(e, 'Erro na compra'));
      await reload();
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="py-24 flex items-center justify-center text-white/40">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ─────────── HERO ─────────── */}
      <div className="rmt-hero rounded-2xl p-5 md:p-7">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-end gap-5">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="rmt-pill bg-[#f0c86f]/15 text-[#f0c86f] border border-[#f0c86f]/30">
                <Coins size={11} /> Mercado de ALZ
              </span>
              {sandbox ? (
                <span className="rmt-pill bg-amber-500/15 text-amber-300 border border-amber-400/30">
                  <FlaskConical size={11} /> Sandbox · compra grátis
                </span>
              ) : (
                <span className="rmt-pill bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
                  <QrCode size={11} /> Pagamento Pix
                </span>
              )}
              <span className="rmt-pill bg-white/5 text-white/60 border border-white/10">
                <span className="rmt-live" /> ao vivo
              </span>
            </div>
            <h1 className="text-2xl md:text-4xl font-black text-white tracking-tight">
              Compre ALZ de outros jogadores
            </h1>
            <p className="text-sm text-white/55 mt-1.5 max-w-xl">
              Cada ALZ à venda já está guardado com a gente. Você paga, ele chega no correio do seu
              personagem — sem troca cara a cara e sem risco de golpe.
            </p>
          </div>
          <div className="shrink-0 text-left lg:text-right">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/45">
              Melhor preço · {fmtAlzShort(unitAlz)}
            </p>
            <p className="text-4xl md:text-5xl font-black rmt-gold tabular-nums leading-none mt-1">
              {stats?.bestPrice ? fmtBrl(stats.bestPrice) : '—'}
            </p>
            {priced.length >= 2 && (
              <p className={`text-sm font-bold mt-1 flex items-center gap-1 lg:justify-end ${change >= 0 ? 'rmt-up' : 'rmt-down'}`}>
                {change >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                {change >= 0 ? '+' : ''}{change.toFixed(1)}% no período
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ─────────── KPIs ─────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={<Coins size={12} />} label="ALZ à venda" value={fmtAlzShort(stats?.alzInOffer || 0)}
             sub={`${stats?.sellersActive || 0} vendedor(es)`} accent="rmt-gold" />
        <Kpi icon={<Zap size={12} />} label="Último negócio" value={stats?.lastPrice ? fmtBrl(stats.lastPrice) : '—'}
             sub={`por ${fmtAlzShort(unitAlz)}`} />
        <Kpi icon={<Activity size={12} />} label="Volume 24h" value={fmtAlzShort(stats?.volumeAlz24h || 0)}
             sub={`${stats?.trades24h || 0} negócio(s)`} />
        <Kpi icon={<Activity size={12} />} label="Volume 7d" value={fmtAlzShort(stats?.volumeAlz7d || 0)}
             sub={`${stats?.trades7d || 0} negócio(s)`} />
        <Kpi icon={<Activity size={12} />} label="Volume 30d" value={fmtAlzShort(stats?.volumeAlz30d || 0)}
             sub={`${stats?.trades30d || 0} negócio(s)`} />
        <Kpi icon={<Users size={12} />} label="Já negociado" value={fmtAlzShort(stats?.totalAlzTraded || 0)}
             sub={`${stats?.totalTrades || 0} no total`} />
      </div>

      {awaiting.length > 0 && (
        <div className="rmt-card rounded-xl p-4 flex flex-wrap items-center gap-3 border-amber-400/30">
          <QrCode size={18} className="text-amber-300" />
          <p className="text-sm text-amber-200 flex-1 min-w-[200px]">
            Você tem {awaiting.length} pedido(s) aguardando o Pix. O ALZ fica reservado até o QR vencer.
          </p>
          <button onClick={() => setPix({ orderId: awaiting[0].orderId })}
                  className="rmt-btn-gold px-4 py-2 rounded-lg text-xs font-black cursor-pointer flex items-center gap-1.5">
            <QrCode size={13} /> Pagar agora
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start">
        <div className="space-y-5 min-w-0">
          {/* ─────────── GRÁFICO ─────────── */}
          <div className="rmt-card rounded-2xl p-5">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <div>
                <h2 className="text-sm font-black text-white uppercase tracking-wider">Preço médio negociado</h2>
                <p className="text-[11px] text-white/45">R$ por {fmtAlzShort(unitAlz)} · barras = volume de ALZ</p>
              </div>
              <div className="ml-auto flex rounded-lg bg-white/5 p-0.5">
                {(['7d', '30d', '1y'] as RmtPeriod[]).map(p => (
                  <button key={p} onClick={() => setPeriod(p)}
                          className={`px-3 py-1 rounded-md text-xs font-bold cursor-pointer transition
                                      ${period === p ? 'bg-[#f0c86f]/20 text-[#f0c86f]' : 'text-white/50 hover:text-white'}`}>
                    {p === '1y' ? '1 ano' : p}
                  </button>
                ))}
              </div>
            </div>
            <PriceChart
              points={(stats?.points || []).map(p => ({
                label: p.label, value: p.price, volume: p.volumeAlz,
                hint: p.trades ? `${p.trades} negócio(s)` : undefined,
              }))}
              format={fmtBrl}
              volumeFormat={(v) => fmtAlzShort(v) + ' ALZ'}
              emptyText="Ainda sem negociações neste período — seja o primeiro."
            />
          </div>

          {/* ─────────── LIVRO DE OFERTAS ─────────── */}
          <div className="rmt-card rounded-2xl overflow-hidden">
            <div className="px-5 py-3.5 flex items-center gap-2 border-b border-white/[0.06]">
              <ShieldCheck size={15} className="text-emerald-400" />
              <h2 className="text-sm font-black text-white uppercase tracking-wider">Ofertas</h2>
              <span className="text-[11px] text-white/40">· ALZ já em custódia · clique pra escolher</span>
              <button onClick={() => reload()} className="ml-auto text-white/45 hover:text-white cursor-pointer" title="Atualizar">
                <RefreshCw size={14} />
              </button>
            </div>
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-5 py-2 text-[10px] font-bold uppercase tracking-wider text-white/35">
              <span>Vendedor</span><span className="text-right">Disponível</span><span className="text-right w-28">R$ / {fmtAlzShort(unitAlz)}</span>
            </div>
            {book.length === 0 ? (
              <div className="py-14 text-center text-white/40 text-sm">Nenhuma oferta no momento.</div>
            ) : book.map((v, i) => (
              <button key={v.userNum} onClick={() => setVendorUserNum(v.userNum)}
                      className={`rmt-row relative w-full grid grid-cols-[1fr_auto_auto] gap-x-4 items-center px-5 py-3
                                  text-left border-t border-white/[0.04] cursor-pointer
                                  ${v.userNum === vendorUserNum ? 'is-active' : ''}`}>
                <span className="rmt-depth" style={{ width: `${(v.availableAlz / maxDepth) * 45}%` }} />
                <span className="relative flex items-center gap-2 min-w-0">
                  {i === 0 && <span className="rmt-pill bg-emerald-500/15 text-emerald-300">melhor</span>}
                  <span className="text-sm font-bold text-white truncate">{v.sellerName}</span>
                  {account?.userNum === v.userNum && <span className="text-[10px] text-white/40">(você)</span>}
                </span>
                <span className="relative text-sm text-white/80 tabular-nums text-right">{fmtAlzShort(v.availableAlz)}</span>
                <span className="relative text-sm font-black rmt-gold tabular-nums text-right w-28">{fmtBrl(v.alzPriceBrl)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ─────────── PAINEL DE COMPRA ─────────── */}
        <div className="xl:sticky xl:top-20 space-y-5">
          <div className="rmt-card rounded-2xl p-5">
            <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 mb-4">
              <ShoppingCart size={15} className="rmt-gold" /> Comprar ALZ
            </h2>

            <label className="text-[11px] font-bold text-white/55 block mb-1.5">Entregar no personagem</label>
            {chars.length === 0 ? (
              <p className="text-[12px] text-amber-400 mb-4">Crie um personagem para receber a compra.</p>
            ) : (
              <select value={charIdx ?? ''} onChange={(e) => setCharIdx(Number(e.target.value))} className="rmt-input mb-4">
                {chars.map(c => (
                  <option key={charId(c)} value={charId(c)}>{c.name} — Nv. {c.lev}</option>
                ))}
              </select>
            )}

            <label className="text-[11px] font-bold text-white/55 block mb-1.5">
              Quantidade <span className="text-white/35">(1 un. = {fmtAlzShort(unitAlz)})</span>
            </label>
            <div className="flex items-center gap-2 mb-2">
              <button onClick={() => setUnits(u => Math.max(1, u - 1))}
                      className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white flex items-center justify-center cursor-pointer">
                <Minus size={15} />
              </button>
              <input type="number" min={1} max={maxUnits} value={units}
                     onChange={(e) => setUnits(Math.min(maxUnits, Math.max(1, Number(e.target.value) || 1)))}
                     className="rmt-input text-center font-black text-lg" />
              <button onClick={() => setUnits(u => Math.min(maxUnits, u + 1))}
                      className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white flex items-center justify-center cursor-pointer">
                <Plus size={15} />
              </button>
            </div>
            <div className="flex gap-1.5 mb-4">
              {[1, 5, 10, 50].filter(n => n <= maxUnits).map(n => (
                <button key={n} onClick={() => setUnits(n)}
                        className={`flex-1 py-1 rounded-md text-[11px] font-bold cursor-pointer
                                    ${units === n ? 'bg-[#f0c86f]/20 text-[#f0c86f]' : 'bg-white/5 text-white/55 hover:text-white'}`}>
                  {n}x
                </button>
              ))}
              <button onClick={() => setUnits(maxUnits)}
                      className={`flex-1 py-1 rounded-md text-[11px] font-bold cursor-pointer
                                  ${units === maxUnits ? 'bg-[#f0c86f]/20 text-[#f0c86f]' : 'bg-white/5 text-white/55 hover:text-white'}`}>
                máx
              </button>
            </div>

            <div className="rounded-xl bg-black/30 border border-white/[0.06] p-4 mb-4">
              <div className="flex justify-between text-xs text-white/50">
                <span>Vendedor</span><span className="text-white font-bold truncate ml-3">{sel?.sellerName || '—'}</span>
              </div>
              <div className="flex justify-between text-xs text-white/50 mt-1.5">
                <span>Preço unitário</span><span className="text-white">{sel ? fmtBrl(sel.alzPriceBrl) : '—'}</span>
              </div>
              <div className="h-px bg-white/[0.06] my-3" />
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-[10px] text-white/45 uppercase font-bold tracking-wider">Você recebe</p>
                  <p className="text-xl font-black text-white tabular-nums">{fmtAlzShort(totalAlz)} <span className="text-xs text-white/40">ALZ</span></p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-white/45 uppercase font-bold tracking-wider">Total</p>
                  <p className="text-2xl font-black rmt-gold tabular-nums">{sandbox ? 'Grátis' : fmtBrl(totalBrl)}</p>
                  {sandbox && totalBrl > 0 && <p className="text-[10px] text-white/35 line-through">{fmtBrl(totalBrl)}</p>}
                </div>
              </div>
            </div>

            <button onClick={handleBuy}
                    disabled={buying || !sel || chars.length === 0 || !!own}
                    className="rmt-btn-gold w-full py-3.5 rounded-xl font-black text-sm flex items-center justify-center gap-2 cursor-pointer">
              {buying ? <Loader2 size={16} className="animate-spin" /> : sandbox ? <Sparkles size={16} /> : <QrCode size={16} />}
              {own ? 'Essa oferta é sua' : sandbox ? 'Comprar (sandbox)' : 'Pagar com Pix'}
            </button>
            <p className="text-[11px] text-white/40 mt-3 flex items-start gap-1.5 leading-snug">
              <Mail size={12} className="shrink-0 mt-0.5" />
              {sandbox
                ? 'Modo de teste: a compra é gratuita e o ALZ chega na hora pelo correio do jogo.'
                : 'Gere o QR, pague no app do banco e o ALZ chega sozinho no correio do jogo. O ALZ fica reservado pra você até o QR vencer.'}
            </p>
          </div>

          {/* ─────────── FITA DE NEGÓCIOS ─────────── */}
          <div className="rmt-card rounded-2xl p-5">
            <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 mb-3">
              <Clock size={14} className="text-[#8fb9de]" /> Últimos negócios
            </h2>
            {(stats?.recentTrades || []).length === 0 ? (
              <p className="text-xs text-white/40 py-4 text-center">Nenhum negócio ainda.</p>
            ) : (
              <div className="rmt-tape space-y-1.5 max-h-64 overflow-hidden">
                {stats!.recentTrades.map((t, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-white/40 tabular-nums">{fmtTime(t.at)}</span>
                    <span className="text-white font-bold tabular-nums">{fmtAlzShort(t.alzAmount)}</span>
                    <span className="rmt-gold font-black tabular-nums">{fmtBrl(t.unitPrice)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─────────── MEUS PEDIDOS ─────────── */}
      <div className="rmt-card rounded-2xl overflow-hidden">
        <div className="px-5 py-3.5 flex items-center gap-2 border-b border-white/[0.06]">
          <ReceiptText size={15} className="text-[#8fb9de]" />
          <h2 className="text-sm font-black text-white uppercase tracking-wider">Meus pedidos</h2>
        </div>
        {orders.length === 0 ? (
          <div className="py-10 text-center text-white/40 text-sm">Você ainda não comprou ALZ.</div>
        ) : orders.map(o => {
          const st = ORDER_STATUS[o.status] || { label: o.status, cls: 'bg-white/10 text-white/60' };
          return (
            <div key={o.orderId} className="rmt-row flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 border-t border-white/[0.04]">
              <span className="text-[11px] text-white/35 font-mono w-14">#{o.orderId}</span>
              <span className="text-sm font-black text-white tabular-nums">{fmtAlzShort(o.alzAmount)} ALZ</span>
              <span className="text-xs text-white/50">→ {o.charName || '—'}</span>
              {o.vendorName && <span className="text-xs text-white/35 hidden md:inline">de {o.vendorName}</span>}
              <span className="ml-auto text-sm font-bold rmt-gold tabular-nums">{o.isSandbox ? 'grátis' : fmtBrl(o.priceBrl)}</span>
              <span className={`rmt-pill ${st.cls}`}>{st.label}</span>
              {o.status === 'awaiting_payment' && (
                <button onClick={() => setPix({ orderId: o.orderId })}
                        className="text-[11px] font-black text-[#f0c86f] hover:underline cursor-pointer">Pagar</button>
              )}
              <span className="text-[11px] text-white/35 w-full md:w-auto">{fmtTime(o.createdAt)}</span>
            </div>
          );
        })}
      </div>

      {pix && (
        <RmtPixDialog
          orderId={pix.orderId}
          initial={pix.initial ? {
            priceBrl: pix.initial.priceBrl, alzAmount: pix.initial.alzAmount, charName: pix.initial.charName,
            qrCodePayload: pix.initial.qrCodePayload, qrCodeImage: pix.initial.qrCodeImage,
            expiresIn: pix.initial.expiresIn,
          } : null}
          onClose={() => { setPix(null); reload(); }}
          onChanged={() => reload()}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ITENS — desligado por enquanto (so' ALZ). A API continua pronta
   (/rmt/items, /rmt/buy-item). Pra religar: importar getItems/buyItem/
   RmtItemMarket + API_URL e voltar com a aba/grade abaixo.

   function ItemIcon({ itemId }: { itemId: number | null }) {
     const [ok, setOk] = useState(true);
     if (!itemId || !ok) return <div className="w-14 h-14 rounded-lg bg-[#0d141b]"><Package size={20} /></div>;
     return <img src={`${API_URL}/admin/item-icon/${itemId}.png?v=nobg2`} onError={() => setOk(false)} />;
   }
   function itemBadges(it: RmtItemMarket) {
     // +upgrade / Divine +N / Extreme +N / N slots / Selado / Vinculado
   }
   const handleBuyItem = async (it: RmtItemMarket) => {
     const res = await buyItem(it.listingId, charIdx);
     notify('success', res.message);
     await fetchItems();
   };
   // grade: items.map(it => <card> ItemIcon + nome + itemBadges + fmtBrl(it.priceBrl) + [Comprar] </card>)
   ═══════════════════════════════════════════════════════════════════════════ */
